import { FormControl } from "@mui/material";
import styled from "styled-components";

import {
  DATA_E2E_KEY,
  getWaypointCategoryVisual,
  IStyledAttributes,
  STATION_ROUTE_WAYPOINT_MAP_BTN_SIZE_PX,
  STATION_ROUTE_WAYPOINT_MAP_ICON_SIZE_PX,
  STATION_ROUTE_WAYPOINT_SEGMENT_OPTIONS_MAX_HEIGHT_PX,
  STATION_ROUTE_WAYPOINT_TOOLBAR_E2E,
  WaypointCategoryEnum,
} from "@ryde/shared";
import { blueGreyMui, deepPurpleMui, greyMui, rydeColors } from "@ryde/theme";

export const StationRouteWaypointToolbarContainer = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: STATION_ROUTE_WAYPOINT_TOOLBAR_E2E,
  },
)`
  pointer-events: auto;
`;

export const StationRouteWaypointMapBtn = styled.span.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "station-route-waypoint-map-btn",
})<{ $active?: boolean; $disabled?: boolean }>`
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  border-radius: 4px;
  border: 1px solid ${blueGreyMui._200};
  background: ${rydeColors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${STATION_ROUTE_WAYPOINT_MAP_BTN_SIZE_PX}px;
  height: ${STATION_ROUTE_WAYPOINT_MAP_BTN_SIZE_PX}px;
  box-sizing: border-box;
  color: ${({ $active }) => ($active ? blueGreyMui._800 : blueGreyMui._700)};
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.2);
  opacity: ${({ $disabled }) => ($disabled ? 0.45 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? "none" : "auto")};

  svg {
    font-size: ${STATION_ROUTE_WAYPOINT_MAP_ICON_SIZE_PX}px;
  }

  &:hover {
    color: ${blueGreyMui._900};
    background: ${greyMui._50};
  }
`;

export const StationRouteWaypointHoverMenu = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "station-route-waypoint-hover-menu",
  },
)`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 168px;
  padding: 3px;
  border-radius: 8px;
  border: 1px solid ${greyMui._300};
  background: ${rydeColors.white};
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.14);

  .menu-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 6px 6px 4px;
    margin-bottom: 2px;
    border-bottom: 1px solid ${greyMui._200};
  }

  .menu-title {
    font-size: 12px;
    font-weight: 600;
    line-height: 1.2;
    color: ${blueGreyMui._800};
  }

  .menu-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    padding: 0;
    border: none;
    border-radius: 4px;
    background: transparent;
    color: ${blueGreyMui._600};
    cursor: pointer;
    flex-shrink: 0;

    &:hover {
      background: ${greyMui._100};
      color: ${blueGreyMui._900};
    }
  }

  .menu-option {
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 7px 10px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    font-weight: 500;
    line-height: 1.2;
    color: ${blueGreyMui._800};
    white-space: nowrap;

    svg {
      font-size: 17px;
      color: ${blueGreyMui._600};
      flex-shrink: 0;
    }

    &:hover {
      background: ${greyMui._100};

      svg {
        color: ${blueGreyMui._800};
      }
    }
  }
`;

export const StationRouteWaypointHoverAnchor = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "station-route-waypoint-hover-anchor",
  },
)`
  position: relative;
  display: inline-flex;
`;

export const StationRouteWaypointPopperPanel = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "station-route-waypoint-popper-panel",
  },
)`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 375px;
  max-width: 425px;
  padding: 14px;
  border-radius: 12px;
  border: 1px solid ${greyMui._200};
  background: ${rydeColors.white}FA;
  backdrop-filter: blur(8px);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.16);

  .panel-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
  }

  .panel-title {
    font-size: 14px;
    font-weight: 700;
    color: ${deepPurpleMui.A700};
    line-height: 1.3;
    flex: 1;
  }

  .panel-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    margin-top: -2px;
    border: none;
    border-radius: 4px;
    background: transparent;
    color: ${blueGreyMui._600};
    cursor: pointer;
    flex-shrink: 0;

    &:hover {
      background: ${greyMui._100};
      color: ${blueGreyMui._900};
    }
  }

  .panel-hint {
    font-size: 12px;
    line-height: 1.45;
    color: ${blueGreyMui._700};
  }

  .panel-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 4px;
  }

  .panel-primary-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 34px;
    padding: 0 14px;
    border-radius: 8px;
    border: none;
    font-size: 13px;
    font-weight: 600;
    color: ${rydeColors.white};
    background: ${deepPurpleMui.A700};
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover:not(.disabled) {
      background: ${deepPurpleMui.A400};
    }

    &.disabled {
      opacity: 0.45;
      cursor: default;
      pointer-events: none;
    }
  }

  .panel-secondary-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 34px;
    padding: 0 12px;
    border-radius: 8px;
    border: 1px solid ${greyMui._300};
    font-size: 13px;
    font-weight: 600;
    color: ${blueGreyMui._800};
    background: ${rydeColors.white};
    cursor: pointer;

    &:hover {
      background: ${greyMui._100};
    }
  }
`;

export const StationRouteWaypointAddressField = styled(
  FormControl,
).attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "station-route-waypoint-address-field",
})`
  width: 100%;

  .addressAutoCompleteInput {
    width: 100%;
  }
`;

export const StationRouteWaypointCustomNameField = styled(
  FormControl,
).attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "station-route-waypoint-custom-name-field",
})`
  width: 100%;
`;

export const StationRouteWaypointPendingMarkerPulse = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "station-route-waypoint-pending-marker",
  },
)`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 3px solid ${deepPurpleMui.A700};
  background: ${deepPurpleMui._500}59;
  box-shadow: 0 0 0 6px ${deepPurpleMui._500}26;
  animation: station-route-waypoint-pulse 1.4s ease-out infinite;
  pointer-events: none;

  @keyframes station-route-waypoint-pulse {
    0% {
      box-shadow: 0 0 0 0 ${deepPurpleMui._500}73;
    }
    70% {
      box-shadow: 0 0 0 12px ${deepPurpleMui._500}00;
    }
    100% {
      box-shadow: 0 0 0 0 ${deepPurpleMui._500}00;
    }
  }
`;

/** Pending waypoint marker rendered inside a MapLibre `<Marker>`. */
export const StationRouteWaypointPendingShell = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 3px solid ${deepPurpleMui.A700};
  background: ${deepPurpleMui._500}59;
  box-shadow: 0 0 0 6px ${deepPurpleMui._500}26;
  animation: station-route-waypoint-pulse 1.4s ease-out infinite;
  pointer-events: none;
`;

export const StationRouteWaypointPendingHost = styled.div`
  .station-route-waypoint-pending-shell {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 3px solid ${deepPurpleMui.A700};
    background: ${deepPurpleMui._500}59;
    box-shadow: 0 0 0 6px ${deepPurpleMui._500}26;
    animation: station-route-waypoint-pulse 1.4s ease-out infinite;
  }

  @keyframes station-route-waypoint-pulse {
    0% {
      box-shadow: 0 0 0 0 ${deepPurpleMui._500}73;
    }
    70% {
      box-shadow: 0 0 0 12px ${deepPurpleMui._500}00;
    }
    100% {
      box-shadow: 0 0 0 0 ${deepPurpleMui._500}00;
    }
  }
`;

export const StationRouteWaypointSegmentPickerStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "station-route-waypoint-segment-picker",
  },
)`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 4px;
  border-top: 1px solid ${greyMui._200};

  .segment-title {
    font-size: 12px;
    font-weight: 700;
    color: ${blueGreyMui._700};
    margin-bottom: 2px;
    flex-shrink: 0;
  }

  .segment-options {
    max-height: ${STATION_ROUTE_WAYPOINT_SEGMENT_OPTIONS_MAX_HEIGHT_PX}px;
    overflow-y: auto;
    overscroll-behavior: contain;
    min-height: 0;
    padding-inline-end: 2px;
  }

  .segment-option {
    margin: 0;
    align-items: center;
    gap: 2px;

    .MuiRadio-root {
      padding: 4px;
    }

    .MuiFormControlLabel-label {
      font-size: 12px;
      line-height: 1.35;
      color: ${blueGreyMui._800};
    }
  }
`;

export const StationRouteWaypointRowBadge = styled.span.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "station-route-waypoint-row-badge",
  },
)<{ $category?: WaypointCategoryEnum }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px dashed
    ${({ $category }) => getWaypointCategoryVisual($category).borderColor};
  color: ${({ $category }) => getWaypointCategoryVisual($category).color};
  background: ${({ $category }) =>
    getWaypointCategoryVisual($category).backgroundColor};
  font-size: 11px;
  font-weight: 700;
`;
